import React, { useEffect, useRef, useState } from 'react';
import CartValueDetails from './CartValueDetails';
import CardProductCart from './CardProductCart';
import CartEmpty from './CartEmpty';
import Button from '../../Form/Button';
import ButtonPrimary from '../../Form/ButtonPrimary';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import { HiXMark } from 'react-icons/hi2';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStoreContext } from '../../../Contexts/StoreContext';
import CartService from '../../../Services/CartService';
import { useLoginContext } from '../../../Contexts/LoginContext';
import { ImSpinner8 } from 'react-icons/im';
import Scrollbars from 'react-custom-scrollbars-2';
import { useCartContext } from '../../../Contexts/CartContext';
import { TbShoppingBagPlus, TbTrash } from 'react-icons/tb';
import { CART_STATUS_FINISHED, ERROR_CART_NOT_FOUND } from '../../../Utils/Constants/Cart';
import { ORDER_STATUS_DONE } from '../../../Utils/Constants/Checkout';
import { useCartHook } from '../../../Hooks/CartHook';
import Confirm from '../Confirm';
import Modal from '../Modal';
import { useStoreHook } from '../../../Hooks/StoreHook';
import { useLayoutContext } from '../../../Contexts/LayoutContext';
import BoxContentEndScrollbars from '../BoxContentEndScrollbars';

interface CartProps {
    onCloseCart: CallableFunction;
    onCartInfo?: CallableFunction;
    onlyInfo?: boolean;
    getCart?: any;
}

const Cart = ({onCloseCart, onCartInfo, onlyInfo=false, getCart}: CartProps) => {

    const { orderType, getOrderTypeIdByName, isStoreCounter, isDirectSale } = useOrderTypeContext();
    const { currentStore, setCashier } = useStoreContext();
    const { token, setShowLoginBasic } = useLoginContext();
    const { cartDisplay, setCartDisplay, setShowCart, setCartFinished, currentCart, loadingCurrentCart, loadingUpdateQuantity } = useCartContext();
    const { cartFinished, cartNotFound, removeAll } = useCartHook();
    const { getLinkToHome } = useStoreHook();
    const { isMobile } = useLayoutContext();

    const [cart, setCart] = useState<any | null>(getCart || null)
    const [loading, setLoading] = useState<Boolean>(isStoreCounter)

    const navigate = useNavigate();
    const location = useLocation();

    const [isCheckout] = useState(location.pathname === `${getLinkToHome()}/checkout`);

    const [showConfirmResetCart, setShowConfirmResetCart] = useState(false);

    const handleCheckout = () => {
        if (token) {
            onCloseCart(false);
            navigate(`${getLinkToHome()}/checkout`);
        } else {
            //* Login básico para outros servicos (diferente do autoatendimento)
            setShowLoginBasic(true);
        }
    }

    const handleCartInfo = (cart: any) => {
        if (onCartInfo) {
            if (isStoreCounter) {
                onCartInfo({
                    total: cart.total_with_discount + cart.extra_tax,
                    empty: cart?.items?.length === 0
                });
            }
        }
    }

    useEffect(() => {
        if (isStoreCounter) {
            const showCart = async () => {
                const payload = {
                    cart_id: cartDisplay.id,
                    store_id: currentStore.id,
                    order_type: Number(getOrderTypeIdByName(orderType)),
                }
                try {
                    const cartService = new CartService(orderType, token);
                    let responseCartShow = await cartService.show(payload);

                    if (responseCartShow) {
                        if (responseCartShow.status === CART_STATUS_FINISHED) {
                            if (parseInt(responseCartShow.order?.status) === ORDER_STATUS_DONE) {
                                cartFinished({id: cartDisplay.id, order: responseCartShow.order});
                                setShowCart(false);
                                setCashier(null);
                                navigate(`${getLinkToHome()}`);
                            } else {
                                setCart(responseCartShow);
                            }
                        } else {
                            handleCartInfo(responseCartShow);

                            const storedCartDisplay = localStorage.getItem('cart_display');

                            if (storedCartDisplay) {
                                const parsedCartDisplay = JSON.parse(storedCartDisplay);
                                const isSameCart = responseCartShow.id === parsedCartDisplay.id;
                                const isDifferentTable = responseCartShow.table?.id !== parsedCartDisplay.table?.id;

                                if (isSameCart && isDifferentTable) {
                                    setCartDisplay({
                                        ...cartDisplay,
                                        table: {
                                            id: responseCartShow.table?.id,
                                            name: responseCartShow.table?.name,
                                        },
                                    });
                                }
                            } else {
                                setCartDisplay({
                                    ...cartDisplay,
                                    table: {
                                        id: responseCartShow.table?.id,
                                        name: responseCartShow.table?.name,
                                    },
                                });
                            }
                            setCartFinished(null);
                            setCart(responseCartShow);
                        }
                    }
                } catch (error: any) {
                    if (error.response?.data?.error === ERROR_CART_NOT_FOUND) {
                        navigate(`${getLinkToHome()}`);
                        cartNotFound();
                    }
                    setCart(null);
                } finally {
                    setLoading(false)
                }
            }
            if (cartDisplay) {
                showCart();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStoreCounter])

    useEffect(() => {
        if (getCart) {
            setCart(getCart);
        }
    }, [getCart]);

    useEffect(() => {
        //* informativo para tela de checkout utilizado em onCartInfo()
        if (!getCart) {
            if (!isStoreCounter && currentCart) {
                const currentCartUpdated = {
                    ...currentCart,
                    empty: currentCart?.items?.length === 0
                };
                setCart(currentCartUpdated);
                if (onCartInfo) {
                    onCartInfo(currentCartUpdated)
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCart]);

    const [showShadow, setShowShadow] = useState(false);
    const handleScrollFrame = (values: any) => {
        const { scrollTop, scrollHeight, clientHeight } = values;
        // Verifica se o scroll está no início ou no fim para controlar a sombra
        if (scrollTop >= 0 && scrollTop + clientHeight < scrollHeight) {
            setShowShadow(true);
        } else {
            setShowShadow(false);
        }
    }

    const currentCartRef = useRef(currentCart);
    useEffect(() => {
        //* Exibição da sombra dos itens quando houver scroll
        const boxScroll = document.getElementById('boxScroll');
        const scrollContent = document.getElementById('scrollContent');

        if (boxScroll && scrollContent) {
            const boxScrollHeight = boxScroll.getBoundingClientRect().height;
            const scrollContentHeight = scrollContent.getBoundingClientRect().height;
            setShowShadow(scrollContentHeight > boxScrollHeight)
        }

        if (currentCartRef.current) {
            if (currentCartRef.current.items.length > 0 && currentCart.items.length === 0) {
                onCloseCart();
                return
            }
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart])

    const handleResetCart = () => {
        setShowConfirmResetCart(false);
        setShowCart(false);
        const payload = {
            order_type: Number(getOrderTypeIdByName(orderType))
        }
        removeAll(currentCart.cart_id, payload);
    }

    return (
        <section className='px-4 h-full'>
            {(loading || loadingCurrentCart)
                ?   <div className="flex items-center justify-center gap-1 h-full text-sm text-gray-500"><ImSpinner8 className="animate-spin" /> <span>{isStoreCounter ? 'Carregando comanda' : 'Carregando carrinho'}</span></div>
                :
                    !cart || cart.items?.length === 0
                        ?
                            <CartEmpty onCloseCart={() => onCloseCart(false)} />
                        :
                            <section className="flex flex-col justify-between h-full gap-2">
                                <div className='relative flex flex-col gap-3 w-full h-full md:overflow-y-auto'>
                                    <div className='relative h-full min-h-24' id="boxScroll">
                                        <Scrollbars autoHide={true}
                                            autoHeight={isCheckout && isMobile}
                                            autoHeightMin={isCheckout && isMobile ? "100%" : undefined}
                                            autoHeightMax={isCheckout && isMobile ? "100%" : undefined}
                                            onScrollFrame={handleScrollFrame}
                                            className='min-h-full'
                                        >
                                            {(isCheckout || onlyInfo) &&
                                                <div className="hidden md:grid mb-4">
                                                    <h2 className="text-sm text-gray-400">Seus produtos</h2>
                                                </div>
                                            }
                                            <div className='flex flex-col divide-y divide-gray-100' id='scrollContent'>
                                                {cart.items?.map((product: any, index: number) =>
                                                    <CardProductCart
                                                        product={product}
                                                        key={index}
                                                        showButtonsIncDec={!isStoreCounter && !onlyInfo}
                                                    />
                                                )}
                                                {(!isStoreCounter && !isCheckout && !onlyInfo && cart.items?.length > 1) &&
                                                    <div className='py-4 pr-2 text-right'>
                                                        <button onClick={() => setShowConfirmResetCart(true)}
                                                            className='inline-flex items-center justify-end gap-0.5 text-xs text-gray-400 hover:text-shopfood-danger font-medium transition-all'
                                                            >
                                                            <TbTrash /> Remover tudo
                                                        </button>
                                                    </div>
                                                }
                                            </div>
                                            <BoxContentEndScrollbars message='Fim dos produtos' />
                                        </Scrollbars>
                                        <div className={`absolute hidden md:block bottom-0 w-full h-2 shadow-reverse-md transition ${showShadow ? 'opacity-100' : 'opacity-0'}`}></div>
                                    </div>
                                    <div className={`absolute -bottom-2 md:hidden w-full h-2 shadow-reverse-md transition ${showShadow ? 'opacity-100' : 'opacity-0'}`}></div>
                                </div>


                                <div className={`grid gap-4 w-full ${isCheckout && isMobile && 'ring-4 ring-white sticky bottom-20 bg-white/95 pt-2'}`}>
                                    <CartValueDetails cart={cart} />
                                    {(!onlyInfo && !isCheckout) &&
                                        <div className='flex gap-2 pb-4'>
                                            <Button onClick={() => onCloseCart(false)} className='text-sm text-nowrap gap-0.5 w-11 h-11'>
                                                <div><TbShoppingBagPlus size={20} /></div>
                                            </Button>
                                            <ButtonPrimary
                                                disabled={ (cart.total_with_discount + cart.extra_tax === 0 ) || loadingUpdateQuantity }
                                                onClick={handleCheckout}
                                                className='w-full text-sm'
                                                >
                                                {isDirectSale ? 'Pagar' : 'Fechar conta'}
                                            </ButtonPrimary>
                                            <Button onClick={() => onCloseCart(false)} className='text-nowrap text-sm w-11 h-11'>
                                                <div><HiXMark size={20} /></div>
                                            </Button>
                                        </div>
                                    }
                                </div>
                            </section>
            }

            <Modal open={showConfirmResetCart} className="max-w-md m-4" onClose={() => setShowConfirmResetCart(false)}>
                <Confirm
                    onConfirm={handleResetCart}
                    onCancel={() => setShowConfirmResetCart(false)}
                    title="Tem certeza?"
                    message='Deseja realmente remover todos os produtos do seu carrinho?'
                    labelConfirm="Sim, quero remover"
                />
            </Modal>
        </section>
    );
};

export default Cart;
